import { Injectable, inject, NgZone } from '@angular/core';
import { Firestore, collectionGroup, query, orderBy, limit, onSnapshot, where, Unsubscribe } from '@angular/fire/firestore';
import type { Map as MapboxMap } from 'mapbox-gl';

/**
 * OBSERVATION CIRCLES
 * -------------------
 * Streams rawObservations and draws each one's stored `circleBoundary.perimeter`
 * as a filled ring. Overlapping circles compound in opacity, so an orbit around
 * a feature renders as a bright flower while a straight traverse stays faint —
 * which is the visual read on whether the investigation logic is firing.
 *
 * Reads the perimeter that was already persisted at observation time rather than
 * regenerating it client-side, so what you see is exactly what the being sensed.
 */

const SOURCE_ID = 'observation-circles';
const FILL_LAYER_ID = 'observation-circles-fill';
const LINE_LAYER_ID = 'observation-circles-line';

export interface ObservationCirclesOptions {
  /** How many recent observations to keep on screen. */
  maxDocs?: number;
  /** Insert layers beneath this existing layer id (e.g. your beings layer). */
  beforeId?: string;
  /** Only draw observations flagged as novel. Cheaper on mobile. */
  novelOnly?: boolean;
}

type LngLat = [number, number];

@Injectable({ providedIn: 'root' })




export class ObservationCirclesService {




  private readonly firestore = inject(Firestore);
  private readonly zone = inject(NgZone);
  private map: MapboxMap | null = null;
  private unsub: Unsubscribe | null = null;
  private beforeId?: string;

  attach(map: MapboxMap, opts: ObservationCirclesOptions = {}): void {
    this.detach();
    this.map = map;
    this.beforeId = opts.beforeId;

    const started = { value: false };
    const start = () => {
      if (started.value) return;
      started.value = true;
      this.ensureLayers();
      console.log('[circles] layers ready', {
        source: !!map.getSource(SOURCE_ID),
        fill: !!map.getLayer(FILL_LAYER_ID),
        line: !!map.getLayer(LINE_LAYER_ID),
      });
      this.subscribe(opts);
    };

    // The style spec being present is the real precondition — isStyleLoaded()
    // stays false while tiles stream in, long after layers can safely be added.
    const styleReady = () => !!map.getStyle()?.layers?.length;

    if (styleReady()) {
      start();
    } else {
      const onReady = () => { if (styleReady()) start(); };
      map.once('style.load', onReady);
      map.once('load', onReady);
      map.once('idle', onReady);   // catch-all: fires whenever rendering settles
    }

    // isStyleLoaded() is false during the initial style fetch; adding a source
    // then throws "Style is not done loading".
    if (map.isStyleLoaded()) start();
    else map.once('style.load', start);
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;

    const map = this.map;
    if (map) {
      for (const id of [FILL_LAYER_ID, LINE_LAYER_ID]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    }
    this.map = null;
  }

  // ================================================================
  // FIRESTORE
  // ================================================================

  private subscribe(opts: ObservationCirclesOptions): void {
    const constraints: any[] = [];
    if (opts.novelOnly) constraints.push(where('comparison.isNovelVisit', '==', true));
    constraints.push(orderBy('timestamp', 'desc'));
    constraints.push(limit(opts.maxDocs ?? 400));

    // NOTE: a collection-group query with orderBy needs a composite index.
    // Firestore logs a console link on first run that creates it in one click.
    const q = query(collectionGroup(this.firestore, 'rawObservations'), ...constraints);

    // onSnapshot fires outside Angular; setData doesn't need change detection,
    // so keep it out of the zone to avoid churning CD on every tick.
    this.unsub = this.zone.runOutsideAngular(() =>
      onSnapshot(
        q,
        // in subscribe(), replace the snapshot handler
        (snap) => {
          const features = snap.docs
            .map((d) => this.toFeature(d.id, d.data() as any))
            .filter((f): f is GeoJSON.Feature => f !== null);

          const dropped = snap.size - features.length;
          const novel = features.filter((f) => f.properties?.['novel']).length;

          console.log('[circles] snapshot', {
            docs: snap.size,
            drawn: features.length,
            dropped,                       // bad/missing perimeter — should be 0
            novel,
            fromCache: snap.metadata.fromCache,
          });

          const source = this.map?.getSource(SOURCE_ID) as any;
          if (!source) {
            console.warn('[circles] ⚠️ source missing — style probably changed under us');
            return;
          }
          source.setData({ type: 'FeatureCollection', features });
        },
        (err) => console.error('[circles] ❌ snapshot error:', err.code, err.message),
      )
    );
  }

  // ================================================================
  // GEOMETRY
  // ================================================================

  /**
   * generateCirclePoints emits N points and stops — index N-1 is not index 0.
   * GeoJSON requires the ring to be explicitly closed or Mapbox silently drops
   * the whole feature, so append the first point if it isn't already there.
   */
  private ringFrom(perimeter: Array<{ lat: number; lng: number }>): LngLat[] | null {
    if (!Array.isArray(perimeter) || perimeter.length < 3) return null;

    const ring: LngLat[] = [];
    for (const p of perimeter) {
      if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lng)) return null;
      ring.push([p.lng, p.lat]);
    }

    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);

    return ring;
  }

  private toFeature(id: string, data: any): GeoJSON.Feature | null {
    const ring = this.ringFrom(data?.circleBoundary?.perimeter);
    if (!ring) return null;

    const nom = data?.nominatim ?? {};

    return {
      type: 'Feature',
      id: this.numericId(id),
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: {
        docId: id,
        tick: data?.tick ?? null,
        novel: data?.comparison?.isNovelVisit === true,
        mode: data?.observationContext?.mode ?? null,
        label: nom.name ?? nom.type ?? data?.terrain?.category ?? 'observation',
        featureKey: nom.osmId ? `${nom.osmType ?? 'n'}:${nom.osmId}` : null,
        radiusMeters: data?.circleBoundary?.radiusMeters ?? null,
      },
    };
  }

  /** Mapbox feature ids must be numeric or numeric-string for feature-state. */
  private numericId(docId: string): number {
    let h = 0;
    for (let i = 0; i < docId.length; i++) h = (h * 31 + docId.charCodeAt(i)) >>> 0;
    return h;
  }

  // ================================================================
  // LAYERS
  // ================================================================

  private ensureLayers(): void {
    const map = this.map;
    if (!map) return;

    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.getLayer(FILL_LAYER_ID)) {
      map.addLayer(
        {
          id: FILL_LAYER_ID,
          type: 'fill',
          source: SOURCE_ID,
          paint: {
            'fill-color': ['case', ['get', 'novel'], '#f97316', '#2563eb'],
            // Deliberately low: overlapping circles compound, so orbit density
            // becomes legible as brightness.
            'fill-opacity': 0.06,
          },
        },
        this.beforeId
      );
    }

    if (!map.getLayer(LINE_LAYER_ID)) {
      map.addLayer(
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': ['case', ['get', 'novel'], '#ea580c', '#3b82f6'],
            'line-width': ['case', ['get', 'novel'], 1.6, 0.6],
            'line-opacity': ['case', ['get', 'novel'], 0.8, 0.35],
          },
        },
        this.beforeId
      );
    }
  }
}